import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as Yup from 'yup';
import { MRT_ColumnDef } from 'material-react-table';
import FormBuilder from '../../../../../../../shared/components/form-builder/form-builder';
import { useApi } from '../../../../../../../shared/hooks/useApi';
import { transformProductData } from '../../../../tc-models/tc-req-dto/tc-component-details.req_dto';
import { Option } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useParams } from 'react-router-dom';
import { TCAppConfig } from '../../../../../../../shared/app-configs/tc-app-configs';
import { ProductShipmentResponse } from '../../certified-products/certified-products.model';

interface TCComponentInformation {
  tcAssociatedProductNo: string;
  tcProductComponentNo: string; //  standardized field matching payload
  tcProductComponentDetail: string;
  tcProductComponentInfoFiberLength: string;
  tcProductComponentInfoYarnCount: string;
  tcProductComponentInfoFabricWeightAndConstruct: string;
  tcProductComponentInfoFabricWeightOfFinishedProduct: string;
  tcProductComponentNetShippingWeight: number | null;
  tcProductComponentInfoAdditional: string;
  tcProductComponentInfoTechnicalSpecifications: string;
}


interface ComponentInfoProps {
  formRef: React.Ref<any>;
  componentData: TCComponentInformation;
  setComponentData: (data: TCComponentInformation) => void;
  productData?: any[];
  onProductRawMaterialCodesChange?: (codes: string[]) => void;
}

const ComponentInfo: React.FC<ComponentInfoProps> = ({ formRef, componentData, setComponentData, productData = [], onProductRawMaterialCodesChange }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState<TCComponentInformation>(componentData);
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [componentDetailOptions, setComponentDetailOptions] = useState<Option[]>([]);
  const [formValues, setFormValues] = useState<TCComponentInformation>(componentData);
  const [expectedNetWeight, setExpectedNetWeight] = useState<number | null>(null);
  const [productCategoryCode, setProductCategoryCode] = useState<string>("");
  const { loading, error, executeGet } = useApi();
  const { TE_ID , formtype} = useParams();
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };
  const fetchProductDetails = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      console.log("Product Info Response:", response?.data);
  
      const result =
        (response?.data?.response || []).map((detail: any) => ({
          value: detail.tcProductArticleNo,   //  use Article No as value
          label: detail.tcProductArticleNo,   // show Article No as label
        })) || [];
  
      // remove duplicates if any
      const uniqueData = result.filter(
        (item : any, index : any, self : any) =>
          index === self.findIndex((t : any) => t.value === item.value)
      );
  
      setProductOptions(uniqueData);
    } catch (err) {
      console.error("Failed to fetch TC Product Info:", err);
    }
  };
  
  useEffect(() => {
    TE_ID && fetchProductDetails()
  }, [])

  const fetchComponentDetails = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_TC_SC_COMPONENT_DETAILS_V4, { tcId: TE_ID });
      const apiData = response?.data || response;
      const scComponentDetails = apiData?.scComponentDetails || [];

      const componentDetailSet = new Set<string>();
      
      // Extract all component details from the nested structure
      scComponentDetails.forEach((scDetail: any) => {
        const componentDetails = scDetail?.componentDetails || [];
        componentDetails.forEach((compDetail: any) => {
          const codes = compDetail?.scProductComponentCode || [];
          codes.forEach((codeWithDesc: string) => {
            if (codeWithDesc && typeof codeWithDesc === 'string') {
              componentDetailSet.add(codeWithDesc);
            }
          });
        });
      });

      // Convert to options array
      // Format: "PC0003; Babies' apparel" -> value: "PC0003", label: "PC0003; Babies' apparel"
      const options = Array.from(componentDetailSet).map((item: string) => {
        const parts = item.split(';');
        const code = parts[0]?.trim() || item;
        return {
          value: code,
          label: item, // Show full string with description
        };
      });

      setComponentDetailOptions(options);
    } catch (err) {
      console.error("Failed to fetch component details:", err);
    }
  };

  useEffect(() => {
    if (TE_ID) {
      fetchComponentDetails();
    }
  }, []);

  // Track last fetched product to avoid refetching
  const lastFetchedProductRef = useRef<string>("");
  // Store callback in ref to avoid dependency issues
  const onProductRawMaterialCodesChangeRef = useRef(onProductRawMaterialCodesChange);
  
  // Update ref when callback changes (this is safe, won't cause loops)
  useEffect(() => {
    onProductRawMaterialCodesChangeRef.current = onProductRawMaterialCodesChange;
  }, [onProductRawMaterialCodesChange]);
  
  // Initialize product category code when componentData changes
  useEffect(() => {
    if (componentData.tcAssociatedProductNo && Array.isArray(productData) && productData.length > 0) {
      const selectedProduct = productData.find(
        (p: any) => p.tcProductArticleNo === componentData.tcAssociatedProductNo
      );
      if (selectedProduct) {
        setProductCategoryCode(selectedProduct.tcProductCategoryCode || "");
      } else {
        setProductCategoryCode("");
      }
    } else {
      setProductCategoryCode("");
    }
  }, [componentData.tcAssociatedProductNo, productData])
  
  // Fetch product raw material codes on initial load (only when product number changes)
  // Removed productData from dependencies to avoid infinite loops - it's accessed via closure
  useEffect(() => {
    const currentProductNo = componentData.tcAssociatedProductNo || "";
    
    // Only fetch if product changed and we have the necessary data
    if (currentProductNo && 
        currentProductNo !== lastFetchedProductRef.current && 
        Array.isArray(productData) && 
        productData.length > 0) {
      
      const selectedProduct = productData.find(
        (p: any) => p.tcProductArticleNo === currentProductNo
      );
      
      if (selectedProduct && selectedProduct.productId && selectedProduct.shipmentId && TE_ID) {
        lastFetchedProductRef.current = currentProductNo;
        
        executeGet(TCAppConfig.GET_PRODUCT_INFO_V4, {
          tcId: TE_ID,
          productId: selectedProduct.productId,
          shipmentId: selectedProduct.shipmentId
        })
        .then((response) => {
          const rawMaterialCodes: string[] = [];
          const productInfo = response?.data?.tcProductInformation?.[0];
          if (productInfo?.tcProductRawMaterialInformation && Array.isArray(productInfo.tcProductRawMaterialInformation)) {
            productInfo.tcProductRawMaterialInformation.forEach((rm: any) => {
              if (rm.tcProductRawMaterialCode) {
                rawMaterialCodes.push(rm.tcProductRawMaterialCode);
              }
            });
          }
          if (onProductRawMaterialCodesChangeRef.current) {
            onProductRawMaterialCodesChangeRef.current(rawMaterialCodes);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch product raw material codes on load:", err);
          if (onProductRawMaterialCodesChangeRef.current) {
            onProductRawMaterialCodesChangeRef.current([]);
          }
        });
      }
    } else if (!currentProductNo && lastFetchedProductRef.current !== "") {
      // Reset when product is cleared
      lastFetchedProductRef.current = "";
      if (onProductRawMaterialCodesChangeRef.current) {
        onProductRawMaterialCodesChangeRef.current([]);
      }
    }
  }, [componentData.tcAssociatedProductNo, TE_ID, executeGet])

  const resetForm = () => {
    setEditInitialValues(componentData);
  };
  

  const handleSubmit = (values: TCComponentInformation) => {
    setComponentData(values);
    toggleDrawer();
  };
  const validationSchema = Yup.object().shape({
    tcAssociatedProductNo: Yup.string().required("Select the product Number"),
    tcProductComponentNo: Yup.string()
      .required("Component Number is required (enter 'na' if not available)")
      .test('lowercase-na', "'na' must be in lowercase", function(value) {
        if (value && value.toLowerCase().trim() === 'na') {
          return value.trim() === 'na';
        }
        return true;
      }),
    tcProductComponentDetail: Yup.string()
      .when("tcProductComponentNo", {
        is: (componentNo: string) => componentNo && componentNo.toLowerCase().trim() !== "na",
        then: (schema) => schema.required("Component Detail is required when Component No is not 'na'"),
        otherwise: (schema) => schema.nullable(),
      }),
    tcProductComponentInfoFiberLength: Yup.string()
      .when([], {
        is: () => {
          const fiberCategories = ["PC0032", "PC0033", "PC0034"];
          return fiberCategories.includes(productCategoryCode);
        },
        then: Yup.string().required("Component Info Fiber Length is required for selected Product Category"),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoYarnCount: Yup.string()
      .when([], {
        is: () => {
          const yarnCategories = ["PC0029", "PC0030", "PC0031"];
          return yarnCategories.includes(productCategoryCode);
        },
        then: Yup.string().required("Component Info Yarn Count is required for selected Product Category"),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightAndConstruct: Yup.string()
      .when([], {
        is: () => {
          const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
          return fabricCategories.includes(productCategoryCode);
        },
        then: Yup.string().required("Component Info Fabric Weight and Construct is required for selected Product Category"),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightOfFinishedProduct: Yup.string().nullable(),
    tcProductComponentNetShippingWeight: Yup.number()
      .typeError("Net Shipping Weight must be a valid number")
      .when("tcProductComponentNo", {
        is: (componentNo: string) => componentNo && componentNo.toLowerCase().trim() !== "na",
        then: (schema) =>
          schema
            .required("Net Shipping Weight is required when Component No is not 'na'")
            .min(0.01, "Net Shipping Weight must be greater than 0 when Component No is not 'na'"),
        otherwise: (schema) => schema.nullable(),
      }),
    tcProductComponentInfoAdditional: Yup.string().nullable(),
    tcProductComponentInfoTechnicalSpecifications: Yup.string().nullable(),
  });
  

  const formConfig = {
    // pageName: "component-details",
    initialValues: componentData,
    validationSchema: validationSchema,
    sections: [
      {
        fields: [
          {
            label: 'Associated Product Article Number',
            name: 'tcAssociatedProductNo',
            type: 'select',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: true,
            readOnly:hasSubmitTc,
            value: componentData.tcAssociatedProductNo,
            events: {
              onChange: async (value: string, allValues: TCComponentInformation, setFieldValue: any) => {
                console.log("onChange triggered with value:", value);
                // Reset the fetch ref when product changes so useEffect can fetch again
                lastFetchedProductRef.current = "";
                
                // Calculate expected net weight when product is selected
                // Add proper null/type checks for productData
                console.log("Product data available:", productData?.length, "items");
                if (Array.isArray(productData) && productData.length > 0) {
                  const selectedProduct = productData.find(
                    (p: any) => p.tcProductArticleNo === value
                  );
                  console.log("Selected product found:", selectedProduct);
                  if (selectedProduct) {
                    const netShipping = selectedProduct.tcProductNetShippingWeight || 0;
                    const supplementary = selectedProduct.tcProductSupplementaryWeight || 0;
                    const calculated = netShipping - supplementary;
                    setExpectedNetWeight(calculated > 0 ? calculated : null);
                    // Store the product category code for conditional validation
                    setProductCategoryCode(selectedProduct.tcProductCategoryCode || "");
                    
                    // Fetch product raw material codes immediately on change
                    if (selectedProduct.productId && selectedProduct.shipmentId && TE_ID) {
                      console.log("Fetching raw material codes for product:", value, selectedProduct);
                      try {
                        const response = await executeGet(TCAppConfig.GET_PRODUCT_INFO_V4, {
                          tcId: TE_ID,
                          productId: selectedProduct.productId,
                          shipmentId: selectedProduct.shipmentId
                        });
                        
                        console.log("Product info response:", response?.data);
                        
                        // Extract raw material codes from response
                        const rawMaterialCodes: string[] = [];
                        const productInfo = response?.data?.tcProductInformation?.[0];
                        if (productInfo?.tcProductRawMaterialInformation && Array.isArray(productInfo.tcProductRawMaterialInformation)) {
                          productInfo.tcProductRawMaterialInformation.forEach((rm: any) => {
                            if (rm.tcProductRawMaterialCode) {
                              rawMaterialCodes.push(rm.tcProductRawMaterialCode);
                            }
                          });
                        }
                        
                        console.log("Extracted raw material codes:", rawMaterialCodes);
                        
                        // Notify parent about raw material codes using ref
                        if (onProductRawMaterialCodesChangeRef.current) {
                          onProductRawMaterialCodesChangeRef.current(rawMaterialCodes);
                        }
                      } catch (err) {
                        console.error("Failed to fetch product raw material codes:", err);
                        // On error, show all raw materials (empty array means show all)
                        if (onProductRawMaterialCodesChangeRef.current) {
                          onProductRawMaterialCodesChangeRef.current([]);
                        }
                      }
                    } else {
                      console.log("Missing productId or shipmentId:", { productId: selectedProduct.productId, shipmentId: selectedProduct.shipmentId, TE_ID });
                      // If productId or shipmentId not available, show all raw materials
                      if (onProductRawMaterialCodesChangeRef.current) {
                        onProductRawMaterialCodesChangeRef.current([]);
                      }
                    }
                  } else {
                    setExpectedNetWeight(null);
                    setProductCategoryCode("");
                    // Clear raw material codes when product is deselected
                    if (onProductRawMaterialCodesChangeRef.current) {
                      onProductRawMaterialCodesChangeRef.current([]);
                    }
                  }
                } else {
                  setExpectedNetWeight(null);
                  setProductCategoryCode("");
                  // Clear raw material codes
                  if (onProductRawMaterialCodesChangeRef.current) {
                    onProductRawMaterialCodesChangeRef.current([]);
                  }
                }
                // Update form values state
                setFormValues((prev) => ({
                  ...prev,
                  tcAssociatedProductNo: value,
                }));
              },
            },
            props: {
              options: productOptions,
              enableSearch:true,
              placeholder: 'Select Product Number',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Number',//renamed to match table hdr
            name: 'tcProductComponentNo',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: true, // Required - use "na" if not applicable
            value: componentData.tcProductComponentNo,
            events: {
              onChange: (value: string, allValues: TCComponentInformation, setFieldValue: any) => {
                // Update form values state in real-time
                setFormValues((prev) => ({
                  ...prev,
                  tcProductComponentNo: value,
                }));
              },
            },
            props: {
              placeholder: 'Enter Component Number',
              variant: 'outlined',
              fullWidth: true,
              helperText: 'If component number is not available, enter "na"',
            },
          },
          {
            label: 'Component Detail',
            name: 'tcProductComponentDetail',
            type: 'select',
            gridSize: { xs: 12, md: 4 },
            required: formValues.tcProductComponentNo?.toLowerCase().trim() !== "na", // Only required when Component No is not "na"
            readOnly: hasSubmitTc,
            value: componentData.tcProductComponentDetail,
            events: {
              onChange: (value: string, allValues: TCComponentInformation, setFieldValue: any) => {
                // Update form values state in real-time
                setFormValues((prev) => ({
                  ...prev,
                  tcProductComponentDetail: value,
                }));
              },
            },
            props: {
              options: componentDetailOptions,
              enableSearch: true,
              placeholder: 'Select Component Detail',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          // {
          //   label: 'Component Info Trims Excluded',
          //   name: 'tcProductComponentInfoTrimsExcluded',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   value: componentData.tcProductComponentInfoTrimsExcluded,
          //   props: {
          //     placeholder: 'Enter Trims Excluded Info',
          //     variant: 'outlined',
          //     fullWidth: true,
          //   },
          // },
          // {
          //   label: 'Component Info Associated TC',
          //   name: 'tcProductComponentInfoAssociatedTc',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 4 },
          //   required: false,
          //   value: componentData.tcProductComponentInfoAssociatedTc,
          //   props: {
          //     placeholder: 'Enter Associated TC',
          //     variant: 'outlined',
          //     fullWidth: true,
          //   },
          // },
          {
            label: 'Component Info Fiber Length',
            name: 'tcProductComponentInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: (() => {
              const fiberCategories = ["PC0032", "PC0033", "PC0034"];
              return fiberCategories.includes(productCategoryCode);
            })(),
            value: componentData.tcProductComponentInfoFiberLength,
            props: {
              placeholder: 'Enter Fiber Length',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Yarn Count',
            name: 'tcProductComponentInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: (() => {
              const yarnCategories = ["PC0029", "PC0030", "PC0031"];
              return yarnCategories.includes(productCategoryCode);
            })(),
            value: componentData.tcProductComponentInfoYarnCount,
            props: {
              placeholder: 'Enter Yarn Count',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Technical Specifications',
            name: 'tcProductComponentInfoTechnicalSpecifications',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: false,
            value: componentData.tcProductComponentInfoTechnicalSpecifications,
            props: {
              placeholder: 'Enter Technical Specifications',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight And Construct (kg)',
            name: 'tcProductComponentInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: (() => {
              const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
              return fabricCategories.includes(productCategoryCode);
            })(),
            value: componentData.tcProductComponentInfoFabricWeightAndConstruct,
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight Of Finished Product (kg)',
            name: 'tcProductComponentInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: false,
            value: componentData.tcProductComponentInfoFabricWeightOfFinishedProduct,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Net Shipping Weight (kg)',
            name: 'tcProductComponentNetShippingWeight',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: formValues.tcProductComponentNo?.toLowerCase().trim() !== "na",
            value: componentData.tcProductComponentNetShippingWeight ?? 0,
            props: {
              type: 'number',
              placeholder: 'Enter Net Shipping Weight (kg)',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          // {
          //   label: 'Component Supplementary Weight (kg)',
          //   name: 'tcProductComponentSupplementaryWeight',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6 },
          //   required: false,
          //   value: componentData.tcProductComponentSupplementaryWeight,
          //   props: {
          //     type: 'number',
          //     placeholder: 'Enter Supplementary Weight (kg)',
          //     variant: 'outlined',
          //     fullWidth: true,
          //   },
          // },
          {
            label: 'Component Info Additional',
            name: 'tcProductComponentInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg:4 },
            required: false,
            value: componentData.tcProductComponentInfoAdditional,
            props: {
              placeholder: 'Enter Additional Info',
              variant: 'outlined',
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      console.log(values,"values")
      setComponentData(values);
      toggleDrawer();
      // resetForm();
    },
  };

  return (
    <>
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default ComponentInfo;
